import React from "react";
import {
  Avatar,
  Box,
  Button,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Images from "app/pages/media/images/Images";
import { PROD_URL } from "app/services/config";
import { getLoggedInCustomerId } from "../accessSystemLoggedInCustomer";

const modalSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  maxHeight: "90vh",
  overflow: "scroll",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

/**
 * Pick an image from customer Documents (Images library), same flow as DSOK app settings / smartkiosk ImagePicker.
 *
 * @param {object} props
 * @param {string} props.fieldPath — Formik path, e.g. `pdfTicketSettings.logoUrl`
 * @param {string} [props.value]
 * @param {(field: string, value: unknown) => void} props.setFieldValue
 * @param {string} props.label
 * @param {string} [props.helperText]
 */
const AccessSystemDocumentImageField = ({
  fieldPath,
  value,
  setFieldValue,
  label,
  helperText,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const [open, setOpen] = React.useState(false);

  const customerId = getLoggedInCustomerId(getAuthUser);

  const handleOpen = () => {
    if (!customerId) {
      enqueueSnackbar(
        t("pages.accessSystemSettings.pdfTicketSettings.missingCustomerId", {
          defaultValue:
            "Nie je k dispozícii ID zákazníka. Obrázok nie je možné vybrať z dokumentov.",
        }),
        { variant: "error" },
      );
      return;
    }
    setOpen(true);
  };

  const handleSelect = (image) => {
    if (!customerId || !image?.filename) {
      setOpen(false);
      return;
    }
    const imageUrl = `${PROD_URL}/uploads/files/${customerId}/Images/${image.filename}`;
    setFieldValue(fieldPath, imageUrl);
    setOpen(false);
  };

  const handleClear = () => {
    setFieldValue(fieldPath, "");
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Box
        onClick={handleOpen}
        sx={{
          width: "100%",
          minHeight: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 1,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
      >
        {value ? (
          <Box sx={{ position: "relative", width: "100%", maxWidth: 360 }}>
            <Avatar
              src={typeof value === "string" ? value : ""}
              variant="rounded"
              sx={{
                height: "auto",
                width: "100%",
                maxHeight: 200,
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("pages.accessSystemSettings.pdfTicketSettings.clickToSelectImage", {
              defaultValue: "Kliknutím vyberte obrázok z dokumentov",
            })}
          </Typography>
        )}
      </Box>
      {value ? (
        <Button size="small" variant="text" color="inherit" onClick={handleClear}>
          {t("pages.accessSystemSettings.pdfTicketSettings.clearImage", {
            defaultValue: "Odstrániť obrázok",
          })}
        </Button>
      ) : null}
      {helperText ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {helperText}
        </Typography>
      ) : null}

      <Modal open={open} onClose={() => setOpen(false)}>
        <Div sx={modalSx}>
          <Images
            layout="row-reverse"
            selectorMode
            allowed={["jpg", "jpeg", "png", "svg", "webp"]}
            customerIdOverride={customerId || undefined}
            onCancel={() => setOpen(false)}
            onSelect={handleSelect}
          />
        </Div>
      </Modal>
    </Stack>
  );
};

export default AccessSystemDocumentImageField;
